import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import { ConfigurationFacade } from '~/app/core/state/configuration/configuration.facade';
import { SalesFacade } from '~/app/domains/sales/sales.facade';
import { ModalService } from '~/app/shared/services/modal/modal.service';
import { HelpOrchestratorModalComponent } from './modals/help-orchestrator/help-orchestrator-modal.component';
import { FormContact } from './types/form-contact.type';

export type HubspotUserData = {
    email: string;
    firstname: string;
    lastname: string;
    company: string;
}

export type HelpModalData = {
    calendlyUrl: string | null;
    formContacts: FormContact[];
    hubspotId: number | null;
    userData: HubspotUserData | null;
}

@Injectable({
    providedIn: 'root',
})
export class HelpFacade {
    private authFacade = inject(AuthenticationFacade);
    private configurationFacade = inject(ConfigurationFacade);
    private salesFacade = inject(SalesFacade);
    private modalService = inject(ModalService);

    private formContacts = toSignal(this.configurationFacade.formContacts$);
    private hubspotId = toSignal(this.configurationFacade.hubspotId$);

    public openHelpModal(): void {
        const calendlyUrl = this.salesFacade.generateCalendlyUrlByCode('SAV');
        const formContacts = this.formContacts() ?? [];
        const hubspotId = this.hubspotId() ?? null;
        const userData = this.getUserData();

        const modalData: HelpModalData = {
            calendlyUrl,
            formContacts,
            hubspotId,
            userData,
        };

        this.modalService.open(
            HelpOrchestratorModalComponent,
            modalData,
            {
                padding: false,
                hasBorder: false,
                isOutsideClickCloseModal: true,
                closable: true,
                overflow: true,
            },
        );
    }

    public getFormContacts(): FormContact[] {
        return this.formContacts() ?? [];
    }

    public hasFormContacts(): boolean {
        return (this.formContacts()?.length ?? 0) > 0;
    }

    private getUserData(): HubspotUserData | null {
        const user = this.authFacade.getUserSnapshot();
        if (!user) {
            return null;
        }
        return {
            email: user.email,
            firstname: user.firstName,
            lastname: user.lastName,
            company: user.company?.name ?? '',
        };
    }
}
